/**
 * Der gezeichnete Ersatz fuer ein fehlendes Portraetfoto.
 *
 * Seit M7 kommen die Portraets als aufbereitete Archivaufnahmen aus
 * `public/assets/portraits/`. Diese Datei ist der Rueckfall: Wer das Projekt
 * frisch auscheckt und `tools/fetch_assets.py` noch nicht laufen liess, soll
 * trotzdem ein Spiel starten koennen, statt in einen leeren Editor zu sehen.
 *
 * Der Stil ist eine Silhouette in Gegenlicht — passt zum Ton des Spiels und
 * verlangt keine Gesichtszuege, die gezeichnet in dieser Groesse ohnehin nur
 * schlecht funktionieren.
 */

interface FallbackLook {
  readonly skinTone: number;
  readonly hairColour: number;
  readonly eyeColour: number;
  readonly bodyType: number;
  readonly hairStyle: number;
  readonly faceMark: number;
}

/**
 * Macht aus einer Portraetkennung immer dieselben Zeichenwerte.
 *
 * Ohne diese Ableitung saehen alle Ersatzbilder gleich aus und das Raster
 * waere unbedienbar — man wuerde fuenfzehnmal dieselbe Silhouette anklicken.
 */
function lookFrom(id: string): FallbackLook {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  const nibble = (shift: number): number => (hash >>> shift) & 0xff;

  return {
    skinTone: nibble(0),
    hairColour: nibble(5),
    eyeColour: nibble(10),
    bodyType: nibble(15),
    hairStyle: nibble(20),
    faceMark: nibble(25),
  };
}

const SKIN_TONES = ['#3b2c24', '#54402f', '#6d523c', '#8a6a4e', '#a98764', '#c6a683'];
const HAIR_COLOURS = ['#14100d', '#2b1f16', '#4a3524', '#6f4b2a', '#8a6a3a', '#9a9086', '#c8c2b8'];
const EYE_COLOURS = ['#3f5d68', '#4a6b46', '#6b5433', '#2f3d4a', '#5a4a55'];

/** Kopfform je Statur — schmal, mittel, breit. */
const HEAD_WIDTH = [0.52, 0.58, 0.66];
const SHOULDER_WIDTH = [0.78, 0.92, 1.06];

function pick<T>(values: readonly T[], index: number): T {
  return values[index % values.length] as T;
}

export function drawFallbackPortrait(id: string, size = 128): HTMLCanvasElement {
  const appearance = lookFrom(id);

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (ctx === null) return canvas;

  const skin = pick(SKIN_TONES, appearance.skinTone);
  const hair = pick(HAIR_COLOURS, appearance.hairColour);
  const eyes = pick(EYE_COLOURS, appearance.eyeColour);
  const headWidth = pick(HEAD_WIDTH, appearance.bodyType) * size * 0.5;
  const shoulders = pick(SHOULDER_WIDTH, appearance.bodyType) * size * 0.5;

  // Hintergrund: kalter Lichtschein, wie von einem Schirm.
  const glow = ctx.createRadialGradient(size / 2, size * 0.42, size * 0.1, size / 2, size * 0.5, size * 0.7);
  glow.addColorStop(0, '#16232e');
  glow.addColorStop(1, '#080d13');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, size, size);

  const centreX = size / 2;
  const headY = size * 0.44;
  const headHeight = headWidth * 1.25;

  // Schultern
  ctx.fillStyle = '#111a22';
  ctx.beginPath();
  ctx.ellipse(centreX, size * 1.02, shoulders, size * 0.3, 0, Math.PI, 0);
  ctx.fill();

  // Hals
  ctx.fillStyle = skin;
  ctx.fillRect(centreX - headWidth * 0.22, headY + headHeight * 0.55, headWidth * 0.44, size * 0.14);

  // Kopf
  ctx.beginPath();
  ctx.ellipse(centreX, headY, headWidth, headHeight, 0, 0, Math.PI * 2);
  ctx.fill();

  // Haare — die Frisur variiert Ansatz und Volumen.
  const style = appearance.hairStyle;
  ctx.fillStyle = hair;
  if (style !== 0) {
    const volume = 0.55 + (style % 4) * 0.14;
    const drop = (style % 3) * 0.22;

    ctx.beginPath();
    ctx.ellipse(centreX, headY - headHeight * 0.28, headWidth * (0.98 + volume * 0.15), headHeight * volume, 0, Math.PI, 0);
    ctx.fill();

    if (drop > 0) {
      // Seitliche Straehnen
      ctx.fillRect(centreX - headWidth * 1.02, headY - headHeight * 0.2, headWidth * 0.22, headHeight * drop * 2);
      ctx.fillRect(centreX + headWidth * 0.8, headY - headHeight * 0.2, headWidth * 0.22, headHeight * drop * 2);
    }
  }

  // Augen
  ctx.fillStyle = eyes;
  const eyeOffset = headWidth * 0.36;
  const eyeY = headY + headHeight * 0.02;
  const eyeRadius = Math.max(1.5, headWidth * 0.1);
  for (const dx of [-eyeOffset, eyeOffset]) {
    ctx.beginPath();
    ctx.ellipse(centreX + dx, eyeY, eyeRadius, eyeRadius * 0.68, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // Gesichtsmerkmal: Narbe, Bart, Brille, Sommersprossen — oder nichts.
  ctx.strokeStyle = 'rgba(0,0,0,0.45)';
  ctx.lineWidth = Math.max(1, size * 0.012);
  switch (appearance.faceMark % 5) {
    case 1: // Narbe über der Braue
      ctx.beginPath();
      ctx.moveTo(centreX + eyeOffset * 0.6, eyeY - headHeight * 0.3);
      ctx.lineTo(centreX + eyeOffset * 1.1, eyeY + headHeight * 0.08);
      ctx.stroke();
      break;
    case 2: // Bart
      ctx.fillStyle = hair;
      ctx.beginPath();
      ctx.ellipse(centreX, headY + headHeight * 0.52, headWidth * 0.72, headHeight * 0.34, 0, 0, Math.PI);
      ctx.fill();
      break;
    case 3: // Brille
      ctx.strokeStyle = 'rgba(200,214,229,0.55)';
      for (const dx of [-eyeOffset, eyeOffset]) {
        ctx.beginPath();
        ctx.ellipse(centreX + dx, eyeY, eyeRadius * 1.9, eyeRadius * 1.6, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
      break;
    case 4: // Sommersprossen
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      for (let i = 0; i < 7; i++) {
        const dx = (i - 3) * headWidth * 0.16;
        ctx.beginPath();
        ctx.arc(centreX + dx, eyeY + headHeight * 0.26 + (i % 2) * 3, Math.max(0.8, size * 0.008), 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    default:
      break;
  }

  return canvas;
}
